import { Box, Button } from "@mui/material";
import React, { useState } from "react";

type StudioPromptBoxProps = {
    disabled: boolean;
    isArchitecturePending: boolean;
    isProposalPending: boolean;
    hasProposalInThread: boolean;
    canUseAi: boolean;
    packageRequired: boolean;
    tt: (key: string, fallback: string) => string;
    onSubmit: (prompt: string) => void;
    onStop: () => void;
};

const StudioPromptBox = React.memo(({
    disabled,
    isArchitecturePending,
    isProposalPending,
    hasProposalInThread,
    canUseAi,
    packageRequired,
    tt,
    onSubmit,
    onStop,
}: StudioPromptBoxProps) => {
    const [value, setValue] = useState("");
    const pending = isArchitecturePending || isProposalPending;
    const submit = () => {
        const nextValue = value.trim();
        if (!nextValue || disabled || !canUseAi) return;

        onSubmit(nextValue);
        setValue("");
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key !== "Enter" || event.shiftKey) {
            return;
        }

        event.preventDefault();
        submit();
    };

    return (
        <Box className="platform-studio__prompt">
            <textarea
                value={value}
                onChange={(event) => setValue(event.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={tt("studio.prompt.placeholder", "Describe what you want to build...")}
                disabled={disabled}
            />
            <Box className="platform-studio__prompt-actions">
                {pending ? (
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={onStop}
                    >
                        {tt("studio.action.stop", "Stop")}
                    </Button>
                ) : null}
                <Button
                    variant="contained"
                    size="small"
                    onClick={submit}
                    disabled={packageRequired || !value.trim() || pending || !canUseAi}
                >
                    {isArchitecturePending
                        ? tt("studio.action.planning", "Planning")
                        : hasProposalInThread
                            ? tt("studio.action.update_proposal", "Update proposal")
                            : tt("studio.action.architect", "Architect")}
                </Button>
            </Box>
        </Box>
    );
});

export default StudioPromptBox;
